import { graphql } from "gatsby";
import React from "react";
import ProcessedImage from "../../../components/common/ProcessedImage/ProcessedImage";
import { Colours } from "../../../utils/colours";
import WrappedSection from "../../WrappedSection/WrappedSection";
import ParagraphTextWithArticles, {
	type ParagraphTextWithArticlesProps,
} from "../ParagraphTextWithArticles/ParagraphTextWithArticles";
import * as styles from "./ParagraphGroupOfExpandableSections.module.scss";

export interface ParagraphGroupOfExpandableSectionsProps {
	title: string;
	text: string;
	expandableSections: ParagraphTextWithArticlesProps[];
	theme: string;
	image?: DogsTrust.MediaImageProps;
	jumpLinksTitle?: string;
}

export const getParagraphGroupOfExpandableSectionsProps = ({
	field_section_title,
	field_jump_links_title,
	field_text,
	field_group_theme = "light",
	relationships: { field_expandable_section, field_featured_image },
}: Queries.ParagraphGroupOfExpandableSectionsDataFragment): ParagraphGroupOfExpandableSectionsProps => {
	const imageSrc = field_featured_image?.field_media_image?.uri || "";
	const imageAlt = field_featured_image?.field_media_image?.alt || "";

	return {
		title: field_section_title,
		jumpLinksTitle: field_jump_links_title,
		text: field_text.value,
		theme: field_group_theme,
		expandableSections: field_expandable_section.map(
			(expandableSection) =>
				({
					heading: expandableSection.field_section_title,
					introText: expandableSection.field_intro_copy.value,
					items: expandableSection.relationships.field_article_picker.map(
						(article) => ({
							priority: article.field_node_weight,
							title: article.title,
							body: article.field_short_description,
							buttonLabel: article.field_short_link_text,
							buttonUrl: article.path.alias,
							imgSrc:
								article.relationships?.field_featured_image?.field_media_image
									?.uri || "",
							label: article.relationships?.field_category_type?.name,
						}),
					),
					images: expandableSection.relationships.field_image.map((image) => ({
						src: image.field_media_image.uri,
						alt: image.field_media_image.alt,
					})),
					showBodyConditionSection:
						expandableSection.field_expandable_enable_bc ?? false,
				}) as ParagraphTextWithArticlesProps,
		),
		image: { src: imageSrc, alt: imageAlt },
	};
};
export const ParagraphGroupOfExpandableSections: React.FC<
	ParagraphGroupOfExpandableSectionsProps
> = ({ title, text, theme, expandableSections, image, jumpLinksTitle }) => {
	const getThemeColour = () => {
		switch (theme) {
			case "alternative":
				return Colours.SANDY_FUR;
			case "light":
				return Colours.WHITE;
			default:
				return Colours.WHITE;
		}
	};
	return (
		<WrappedSection
			sectionName="ParagraphGroupOfExpandableSections"
			sectionColour={getThemeColour()}
		>
			<header className={styles.headercontainer}>
				<div className={styles.details}>
					{jumpLinksTitle ? (
						<h2 data-jumplinkstitle={jumpLinksTitle}>{title}</h2>
					) : (
						<h2>{title}</h2>
					)}
					<div
						// biome-ignore lint/security/noDangerouslySetInnerHtml: Safe source
						dangerouslySetInnerHTML={{
							__html: text,
						}}
					/>
				</div>

				{image.src && (
					<div className={styles.imagecontainer}>
						<ProcessedImage
							src={image.src}
							dimensions="600x450"
							width={600}
							height={450}
							alt={image.alt}
							loading={"lazy"}
						/>
					</div>
				)}
			</header>

			{expandableSections.map((section) => (
				<ParagraphTextWithArticles
					key={section.heading}
					{...section}
					isSubComponent={true}
					subComponentTheme={theme}
					className={styles.expandablesection}
				/>
			))}
		</WrappedSection>
	);
};

export default ParagraphGroupOfExpandableSections;

export const fragment = graphql`
    fragment ParagraphGroupOfExpandableSectionsData on paragraph__group_of_expandable_sections {
        __typename
        id
        field_section_title
		field_jump_links_title
        field_text {
            value
        }
        field_group_theme
        relationships {
            field_expandable_section {
                ...ParagraphTextWithArticlesData
            }
            field_featured_image {
                ...MediaImageData
            }
        }
    }
`;
